import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';
import { BankStatement, BankStatementCreate, BankStatementUpdate } from '../models/BankStatement';
import { BankTransaction } from '../models/BankTransaction';

export interface BankStatementPagedResult {
  items: BankStatement[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class BankStatementsService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('bankstatements', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  getStatementTransactions(id: number): Observable<BankTransaction[]> {
    return this.http.get<BankTransaction[]>(this.financialUrl(`/${id}/transactions`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações do extrato');
        return throwError(() => err);
      })
    );
  }

  getByBank(bank: string, start?: string, end?: string, options?: PaginationOptions): Observable<BankStatementPagedResult> {
    let params = new HttpParams();
    if (start) params = params.set('start', start);
    if (end) params = params.set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/bank/${encodeURIComponent(bank)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar extratos por banco');
        return throwError(() => err);
      })
    );
  }

  getAll(): Observable<any[]> {
    return this.http.get<any[]>(`${this.financialUrl()}`).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar extratos bancários');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<BankStatement> {
    return this.http.get<BankStatement>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar extrato bancário');
        return throwError(() => err);
      })
    );
  }

  create(payload: BankStatementCreate): Observable<BankStatement> {
    return this.http.post<BankStatement>(`${this.financialUrl()}`, payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar extrato bancário');
        return throwError(() => err);
      })
    );
  }

  update(id: number, payload: BankStatementUpdate): Observable<BankStatement> {
    return this.http.put<BankStatement>(this.financialUrl(`/${id}`), payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar extrato bancário');
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao deletar extrato bancário');
        return throwError(() => err);
      })
    );
  }

  private mapPagedResponse(response: any): BankStatementPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems;

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
